import React from 'react';
import { Group, Text, Avatar, Menu, Button, Anchor, ActionIcon } from '@mantine/core';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import { IconWorld } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

function getInitials(user) {
  const name = user?.displayName || user?.email || '';
  const parts = String(name).trim().split(/\s+|@/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const second = parts[1]?.[0] || '';
  return (first + second).toUpperCase() || 'U';
}

export default function AppHeader() {
  const location = useLocation();
  const [user, setUser] = React.useState(() => auth.currentUser);
  const navigate = useNavigate();
  // Language managed by i18next; persist via quart_lang for landing compatibility
  const { t, i18n } = useTranslation('common');
  const isLanding = location.pathname === '/';
  const ahref = (hash) => (isLanding ? `#${hash}` : `/#${hash}`);
  const labels = React.useMemo(() => ({
    features: t('landing.nav.features'),
    how: t('landing.nav.how'),
    pricing: t('landing.nav.pricing'),
    cta: t('landing.cta_header'),
  }), [t]);

  const toggleLang = () => {
    const next = (i18n.language && i18n.language.startsWith('fr')) ? 'en' : 'fr';
    try { localStorage.setItem('quart_lang', next); } catch {}
    try { window.dispatchEvent(new Event('quart-lang-changed')); } catch {}
    try { i18n.changeLanguage(next); } catch {}
  };

  // No breadcrumb/name lookup needed in the header; keep navbar minimal
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  return (
    <Group justify="space-between" align="center" px="md" style={{ height: 56 }}>
      <Group align="center" gap="md">
        <Text component={Link} to="/" className="logo header-logo" style={{ textDecoration: 'none' }}>
          Quart
        </Text>
        <Group visibleFrom="sm" gap="md">
          <Anchor href={ahref('features')} c="dimmed">{labels.features}</Anchor>
          <Anchor href={ahref('how')} c="dimmed">{labels.how}</Anchor>
          <Anchor href={ahref('pricing')} c="dimmed">{labels.pricing}</Anchor>
        </Group>
      </Group>
      <Group align="center" gap="sm">
      <ActionIcon variant="subtle" onClick={toggleLang} title={t('lang.toggle')}>
        <IconWorld size={18} />
      </ActionIcon>
      {user ? (
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <Avatar radius="xl" color="blue" style={{ cursor: 'pointer' }} title={user?.email || user?.displayName || 'User'}>
              {getInitials(user)}
            </Avatar>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{user?.displayName || user?.email || 'Account'}</Menu.Label>
            <Menu.Item onClick={() => navigate('/account')}>My Account</Menu.Item>
            <Menu.Item onClick={() => navigate('/app')}>Dashboard</Menu.Item>
            <Menu.Item onClick={() => signOut(auth)}>Sign out</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        <Button component={Link} to="/auth" variant="filled" size="xs">
          {labels.cta}
        </Button>
      )}
      </Group>
    </Group>
  );
}
